import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { Form } from "../../components/Form";
import { Input } from "../../components/Input";
import { SelectInput } from "../../components/SelectInput";
import { Toggle } from "../../components/Toggle";
import { spacing } from "@styling/sizing";
import { PrepStationScope } from "@KassaroAB/shared/types/enums/PrepStationScope";
import { ServingScope } from "@KassaroAB/shared/types/enums/ServingScope";
import type {
  RestCreatePrepStation,
  RestPrepStation,
} from "@KassaroAB/shared/types/prepStation";
import { FormSection } from "../../components/FormSection";
import { useCatalogScope } from "../../hooks/useCatalogScope";
import { useGetPrepStationsQuery } from "../../query/prepStationsQuery";

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  font-size: 13px;
  cursor: pointer;
`;

interface PrepStationFormProps {
  formId: string;
  prepStation?: RestPrepStation;
  savedTick?: number;
  onSubmit: (data: RestCreatePrepStation) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const PrepStationForm = ({
  formId,
  prepStation,
  savedTick,
  onSubmit,
  onDirtyChange,
}: PrepStationFormProps) => {
  const { t } = useTranslation();
  const { scope } = useCatalogScope();
  const { data: prepStations } = useGetPrepStationsQuery(scope);
  const otherStations = (prepStations ?? []).filter(
    (station) => station.id !== prepStation?.id && !station.isMaster,
  );

  const form = useForm({
    defaultValues: {
      name: prepStation?.name ?? "",
      servingScope: prepStation?.servingScope ?? ServingScope.ONLY_THIS,
      prepStationScope:
        prepStation?.prepStationScope ?? PrepStationScope.ONLY_THIS,
      simplifyItemsFromOtherPrepStations:
        prepStation?.simplifyItemsFromOtherPrepStations ?? true,
      requireReference: prepStation?.requireReference ?? false,
      onePrepTicketPerCourse: prepStation?.onePrepTicketPerCourse ?? false,
      isMaster: prepStation?.isMaster ?? false,
      mirroredPrepStationIds: prepStation?.mirroredPrepStationIds ?? [],
    },
    onSubmit: ({ value }) => {
      onSubmit({
        name: value.name,
        printerIds: prepStation?.printerIds ?? [],
        printerOverrides: prepStation?.printerOverrides,
        servingScope: value.servingScope,
        prepStationScope: value.prepStationScope,
        simplifyItemsFromOtherPrepStations:
          value.simplifyItemsFromOtherPrepStations,
        requireReference: value.requireReference,
        onePrepTicketPerCourse: value.onePrepTicketPerCourse,
        isMaster: value.isMaster,
        mirroredPrepStationIds: value.isMaster
          ? value.mirroredPrepStationIds
          : [],
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);
  const isMaster = useStore(form.store, (state) => state.values.isMaster);

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!savedTick) return;
    form.reset(form.state.values);
  }, [savedTick, form]);

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <FormSection title={t("prepStationForm.basicInformation")}>
        <Fields>
          <form.Field name="name">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("prepStationForm.name")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="servingScope">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("prepStationForm.servingScope")}</FormLabel>
                <SelectInput
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(e.target.value as ServingScope)
                  }
                  onBlur={field.handleBlur}
                >
                  {Object.values(ServingScope).map((scope) => (
                    <option key={scope} value={scope}>
                      {t(`prepStationForm.servingScope${scope}`)}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="prepStationScope">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("prepStationForm.prepStationScope")}</FormLabel>
                <SelectInput
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(e.target.value as PrepStationScope)
                  }
                  onBlur={field.handleBlur}
                >
                  {Object.values(PrepStationScope).map((scope) => (
                    <option key={scope} value={scope}>
                      {t(`prepStationForm.prepStationScope${scope}`)}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="simplifyItemsFromOtherPrepStations">
            {(field) => (
              <FormGroup>
                <FormLabel>
                  {t("prepStationForm.simplifyItemsFromOtherPrepStations")}
                </FormLabel>
                <Toggle
                  checked={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="requireReference">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("prepStationForm.requireReference")}</FormLabel>
                <Toggle
                  checked={!!field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="onePrepTicketPerCourse">
            {(field) => (
              <FormGroup>
                <FormLabel>
                  {t("prepStationForm.onePrepTicketPerCourse")}
                </FormLabel>
                <Toggle
                  checked={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>

      <FormSection title={t("prepStationForm.master")}>
        <Fields>
          <form.Field name="isMaster">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("prepStationForm.isMaster")}</FormLabel>
                <Toggle
                  checked={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          {isMaster && (
            <form.Field name="mirroredPrepStationIds">
              {(field) => (
                <FormGroup>
                  <FormLabel>
                    {t("prepStationForm.mirroredPrepStations")}
                  </FormLabel>
                  <CheckboxList>
                    {otherStations.map((station) => (
                      <CheckboxLabel key={station.id}>
                        <input
                          type="checkbox"
                          checked={field.state.value.includes(station.id)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...field.state.value, station.id]
                              : field.state.value.filter(
                                  (id) => id !== station.id,
                                );
                            field.handleChange(next);
                          }}
                        />
                        {station.name}
                      </CheckboxLabel>
                    ))}
                  </CheckboxList>
                </FormGroup>
              )}
            </form.Field>
          )}
        </Fields>
      </FormSection>
    </Form>
  );
};
